import * as React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PlatformUtils } from './shared/utils';
import { CerebroNavProvider, Urls, useAuth } from './shared/context';
import { Layout } from './shared/components/Layout';
import {
	MatchingTranches,
	Settings,
	Documents,
	Teasers,
	LoanApplication,
	LenderNda,
	Nda,
	RelatedDeals,
	LenderInvitesPage,
} from './pages';
import { NavigationBlockerProvider } from './shared/context/NavigationBlockerContext';
import EmployeeCompanyRoute from './shared/components/Layout/EmployeeCompanyRoute';
import EmployeeRoute from './shared/components/Layout/EmployeeRoute';
import { LenderParticipants } from './pages/Participants/Lenders';
import { BorrowerParticipants } from './pages/Participants/Borrowers';
import { PartnerParticipants } from './pages/Participants/Partners';
import LenderRoute from './shared/components/Layout/LenderRoute';
import { TeamMemberParticipants } from './pages/Participants/TeamMembers';
import { TermSheets } from './pages/TermSheets';
import { ComparisonReport } from './pages/TermSheets/ComparisonReport';
import { RevisionForm } from './pages/TermSheets/Revision';
import { Milestones } from './pages/Milestones';
import { Calls } from './pages/Calls';
import { Summary } from './pages/Summary';
import { ActivityLog } from './pages/ActivityLog';
import { DiligenceRequests } from './pages/DiligenceRequests';
import { LenderSummaryReport } from './pages/Milestones/LenderSummaryReports';
import { ActiveLenderSummaryReport } from './pages/Milestones/ActiveLenderSummaryReport';

export const App: React.FC = () => {
	const auth = useAuth();

	if (!auth.isAuthenticated) {
		PlatformUtils.RedirectToPlatform();
		return null;
	}

	return !auth.isLoading ? (
		<Routes>
			<Route
				path="/"
				element={
					<NavigationBlockerProvider>
						<CerebroNavProvider>
							<Layout />
						</CerebroNavProvider>
					</NavigationBlockerProvider>
				}
			>
				<Route path="/:guid">
					<Route element={<EmployeeCompanyRoute />}>
						<Route path={Urls.Summary.Root} element={<Summary />} />
					</Route>
					<Route path={Urls.LoanApplication.Root} element={<LoanApplication />} />
					<Route path={Urls.Documents.Root} element={<Documents />} />
					<Route path={Urls.DocumentFolder.Root} element={<Documents />} />

					<Route element={<EmployeeCompanyRoute />}>
						<Route path={Urls.Teasers.Root} element={<Teasers />} />
						<Route path={Urls.LenderNda.Root} element={<LenderNda />} />
					</Route>

					<Route element={<EmployeeCompanyRoute />}>
						<Route path={Urls.Participants.Root} element={<LenderParticipants />} />
						<Route path={Urls.Participants.Borrowers} element={<BorrowerParticipants />} />
						<Route path={Urls.Participants.Partners} element={<PartnerParticipants />} />
					</Route>

					<Route element={<LenderRoute excludeTeamMembers={true} />}>
						<Route path={Urls.TeamMembers.Root} element={<TeamMemberParticipants />} />
					</Route>

					<Route element={<LenderRoute excludeTeamMembers={false} />}>
						<Route path={Urls.Nda.Root} element={<Nda />} />
					</Route>

					<Route element={<EmployeeCompanyRoute />}>
						<Route path={Urls.Milestones.Root} element={<Milestones />} />
						<Route path={Urls.Milestones.LenderSummary} element={<LenderSummaryReport />} />
						<Route path={Urls.Milestones.ActiveLenderSummary} element={<ActiveLenderSummaryReport />} />
					</Route>
					<Route path={Urls.Calls.Root} element={<Calls />} />
					<Route path={Urls.DiligenceRequests.Root} element={<DiligenceRequests />} />
					<Route path={Urls.TermSheets.Root} element={<TermSheets />} />

					<Route element={<EmployeeRoute />}>
						<Route path={Urls.TermSheets.RevisionForm} element={<RevisionForm />} />
						<Route path={Urls.TermSheets.ComparisonReport} element={<ComparisonReport />} />
						<Route path={Urls.Matching.Root} element={<MatchingTranches />} />
						<Route path={Urls.Settings.Root} element={<Settings />} />
						<Route path={Urls.LenderInvites.Root} element={<LenderInvitesPage />} />
					</Route>
					<Route path={Urls.ActivityLog.Root} element={<ActivityLog />} />
					<Route path={Urls.RelatedDeals.Root} element={<RelatedDeals />} />

					<Route index element={<Navigate to={Urls.Documents.Root} />} />
					<Route path="*" element={<Navigate to={Urls.Documents.Root} />} />
				</Route>
				<Route index element={<p>404</p>} />
			</Route>
		</Routes>
	) : (
		<></>
	);
};
